import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin, Youtube, Twitter, Music2, ArrowRight } from "lucide-react";
import { BRAND, CATEGORIES } from "../lib/constants";
import { useLang } from "../lib/lang";
import api from "../lib/api";

// Module-scoped so React doesn't see a new component identity each render.
const SocialIcon = ({ href, label, testid, children }) =>
  href ? (
    <a href={href} target="_blank" rel="noreferrer" aria-label={label} className="w-10 h-10 border border-white/15 hover:border-[#B08858] hover:bg-[#B08858] hover:text-white transition flex items-center justify-center" data-testid={testid}>
      {children}
    </a>
  ) : null;

const CAT_KEYS = {
  "Kitchen Cabinets": "cat.kitchen",
  "Bedroom Wardrobes": "cat.bedroom",
  "Bathroom Vanities": "cat.bathroom",
  "TV Panels": "cat.tv",
  "Walk-in Closets": "cat.closet",
  "Living Room": "cat.living",
  "Loose Furniture": "cat.office",
  "Doors & Windows": "cat.doors",
};

export default function Footer() {
  const { t, lang } = useLang();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const facebook = settings?.facebook_url ?? BRAND.facebookUrl;
  const instagram = settings?.instagram_url ?? BRAND.instagramUrl;
  const linkedin = settings?.linkedin_url ?? BRAND.linkedinUrl;
  const youtube = settings?.youtube_url;
  const twitter = settings?.twitter_url ?? BRAND.twitterUrl;
  const tiktok = settings?.tiktok_url ?? BRAND.tiktokUrl;

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A0A0A] text-[#F5F2EB] relative overflow-hidden"
    >
      {/* Design Partner CTA strip — removed per client request (Feb 2026). The
          Design Partner role now lives only in /careers, not in the site-wide
          footer. */}

      <div className="container-x pt-24 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <img
              src={BRAND.logoUrl}
              alt="Theo-Oppolia"
              width="240"
              height="80"
              loading="lazy"
              decoding="async"
              className="h-20 w-auto mb-6"
            />
            <p className="text-sm text-[#F5F2EB]/70 leading-relaxed max-w-md">
              {t("footer.tagline")}
            </p>
            <div className="flex gap-4 mt-8">
              <SocialIcon href={instagram} label="Instagram" testid="social-instagram"><Instagram size={16} /></SocialIcon>
              <SocialIcon href={facebook} label="Facebook" testid="social-facebook"><Facebook size={16} /></SocialIcon>
              <SocialIcon href={linkedin} label="LinkedIn" testid="social-linkedin"><Linkedin size={16} /></SocialIcon>
              <SocialIcon href={youtube} label="YouTube" testid="social-youtube"><Youtube size={16} /></SocialIcon>
              <SocialIcon href={twitter} label="X (Twitter)" testid="social-twitter"><Twitter size={16} /></SocialIcon>
              <SocialIcon href={tiktok} label="TikTok" testid="social-tiktok"><Music2 size={16} /></SocialIcon>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="overline text-[#FF6A00] mb-5">{t("footer.collections")}</p>
            <ul className="space-y-3">
              {CATEGORIES.slice(0, 6).map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/products?cat=${encodeURIComponent(c.slug)}`}
                    className="text-sm text-[#F5F2EB]/80 hover:text-[#FF6A00] transition"
                    data-testid={`footer-cat-${c.slug.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {CAT_KEYS[c.slug] ? t(CAT_KEYS[c.slug]) : c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions column — surfaces the four launch-critical service
              pages (Installation & Remodeling, Builders, Developers, Trade
              Partners). Without this column these pages were only reachable
              from the homepage or by direct URL. Bilingual — Feb 2026. */}
          <div className="md:col-span-2">
            <p className="overline text-[#FF6A00] mb-5">{t("footer.solutions")}</p>
            <ul className="space-y-3">
              <li>
                <Link to="/installation-remodeling"
                      className="text-sm text-[#F5F2EB]/80 hover:text-[#FF6A00] transition"
                      data-testid="footer-installation">
                  {t("footer.solutions.installation")}
                </Link>
              </li>
              <li>
                <Link to="/installation/request"
                      className="text-sm text-[#F5F2EB]/80 hover:text-[#FF6A00] transition"
                      data-testid="footer-installation-request">
                  Request installation service
                </Link>
              </li>
              <li>
                <Link to="/builder"
                      className="text-sm text-[#F5F2EB]/80 hover:text-[#FF6A00] transition"
                      data-testid="footer-builder">
                  {t("footer.solutions.builder")}
                </Link>
              </li>
              <li>
                <Link to="/developer"
                      className="text-sm text-[#F5F2EB]/80 hover:text-[#FF6A00] transition"
                      data-testid="footer-developer">
                  {t("footer.solutions.developer")}
                </Link>
              </li>
              <li>
                <Link to="/trade-partner"
                      className="text-sm text-[#F5F2EB]/80 hover:text-[#FF6A00] transition"
                      data-testid="footer-trade-partner">
                  {t("footer.solutions.partner")}
                </Link>
              </li>
              <li>
                <Link to="/consultation"
                      className="text-sm text-[#F5F2EB]/80 hover:text-[#FF6A00] transition"
                      data-testid="footer-consultation-link">
                  {t("footer.solutions.consultation")}
                </Link>
              </li>
              <li>
                <Link to="/rfq"
                      className="text-sm text-[#F5F2EB]/80 hover:text-[#FF6A00] transition"
                      data-testid="footer-rfq">
                  {t("footer.solutions.rfq")}
                </Link>
              </li>
              <li>
                <Link to="/faq"
                      className="text-sm text-[#F5F2EB]/80 hover:text-[#FF6A00] transition"
                      data-testid="footer-faq">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="overline text-[#FF6A00] mb-5">
              {t("footer.showroom")} <span className="text-[#FF6A00]/70 text-[10px] ml-1">· {t("common.opening_soon")}</span>
            </p>
            <ul className="space-y-4 text-sm text-[#F5F2EB]/80">
              <li className="flex gap-3">
                <MapPin size={16} className="shrink-0 mt-0.5 text-[#FF6A00]" />
                <span>{BRAND.address}</span>
              </li>
              <li>
                <a href={`tel:${BRAND.phoneRaw}`} className="flex gap-3 hover:text-[#FF6A00]" data-testid="footer-phone-general">
                  <Phone size={16} className="shrink-0 mt-0.5 text-[#FF6A00]" />
                  <span>
                    {BRAND.phone}
                    <span className="block text-[10px] tracking-[0.15em] uppercase text-[#F5F2EB]/45">General</span>
                  </span>
                </a>
              </li>
              <li>
                <a href={`tel:${BRAND.phoneSalesRaw}`} className="flex gap-3 hover:text-[#FF6A00]" data-testid="footer-phone-sales">
                  <Phone size={16} className="shrink-0 mt-0.5 text-[#FF6A00]" />
                  <span>
                    {BRAND.phoneSales}
                    <span className="block text-[10px] tracking-[0.15em] uppercase text-[#F5F2EB]/45">Sales</span>
                  </span>
                </a>
              </li>
              <li>
                <a href={`mailto:${BRAND.email}`} className="flex gap-3 hover:text-[#FF6A00]" data-testid="footer-email">
                  <Mail size={16} className="shrink-0 mt-0.5 text-[#FF6A00]" />
                  <span>
                    {BRAND.email}
                    <span className="block text-[10px] tracking-[0.15em] uppercase text-[#F5F2EB]/45">General enquiries</span>
                  </span>
                </a>
              </li>
              <li>
                <a href={`mailto:${BRAND.emailSales}`} className="flex gap-3 hover:text-[#FF6A00]" data-testid="footer-email-sales">
                  <Mail size={16} className="shrink-0 mt-0.5 text-[#FF6A00]" />
                  <span>
                    {BRAND.emailSales}
                    <span className="block text-[10px] tracking-[0.15em] uppercase text-[#F5F2EB]/45">Quotes & sales</span>
                  </span>
                </a>
              </li>
              <li className="text-xs text-[#F5F2EB]/50 pl-7">{BRAND.website}</li>
              <li className="text-xs text-[#F5F2EB]/50 pl-7 pt-2">
                Global brand:{" "}
                <a
                  href={BRAND.parentSiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#FF6A00] hover:underline"
                  data-testid="footer-parent-site"
                >
                  {BRAND.parentSite}
                </a>
              </li>
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/consultation" className="btn-sharp primary text-center" data-testid="footer-consultation-cta">
                {t("footer.book")}
              </Link>
              <Link to="/catalog" className="btn-sharp outline-light text-center" data-testid="footer-catalog-cta">
                {t("footer.catalog")}
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-[#F5F2EB]/50">
          <p>{t("footer.copyright", { year: new Date().getFullYear() })}</p>
          <p className="flex gap-4">
            {/* rel="nofollow" — /admin is Disallow'd in robots.txt so we
                explicitly signal crawlers not to follow the link. */}
            <Link to="/admin/login" rel="nofollow" className="hover:text-[#FF6A00]" data-testid="admin-link">{t("footer.admin")}</Link>
            <span>{lang === "fr" ? "Construit selon le standard du Groupe OPPEIN." : "Built on the OPPEIN Group standard."}</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
